import type { RefObject } from 'react';
import React, { useRef } from 'react';
import classNames from 'clsx';
import Link from '@wix/thunderbolt-elements/components/Link';
import {
  formatClassNames,
  isEmptyObject,
} from '@wix/editor-elements-common-utils';
import type { IMenuItemProps } from '../../../Menu.types';
import { createSDKAction } from './utils';
import classes from './style/MenuItem.scss';
import { AnimationParts, testIds } from '../../constants';
import { isCurrentItem } from '../../../../../common/menu/getCurrentMenuItem';
import { semanticClassNames as menuSemanticClassNames } from '../../../Menu.semanticClassNames';
import shmSemanticClassNames from '../../../../StylableHorizontalMenu/StylableHorizontalMenu.semanticClassNames';
import DropdownIcon from '../../assets/dropdownIcon.svg';

const itemLabelClassNames = classNames(
  classes.label,
  formatClassNames(shmSemanticClassNames.menuItemLabel),
  formatClassNames(menuSemanticClassNames.itemLabel),
);

export const MenuItemLabel: React.FC<
  IMenuItemProps & {
    hasDropdownMenu: boolean;
    hasSubItems: boolean;
    chevronButtonRef: any;
    setIsOpen: (cb: (prevIsHovered: boolean) => boolean) => void;
    isOpen: boolean;
  }
> = props => {
  const {
    item,
    currentItem,
    hasDropdownMenu,
    hasSubItems,
    onItemClick,
    onItemDblClick,
    onItemMouseIn,
    onItemMouseOut,
    partToPreviewStateMap,
    translations,
    chevronButtonRef,
    setIsOpen,
    isOpen,
  } = props;

  const isCurrentPage = isCurrentItem(item, currentItem);
  const { label, link } = item;
  const hasLink = !isEmptyObject(link);
  const linkRef: RefObject<HTMLAnchorElement> = useRef<HTMLAnchorElement>(null);

  const labelContainerClassNames = classNames(
    classes.labelContainer,
    formatClassNames(shmSemanticClassNames.menuItemWrapper),
    formatClassNames(menuSemanticClassNames.item),
  );

  const handleDropdownIconClick = () => {
    setIsOpen(currentIsOpen => !currentIsOpen);
  };

  const isInteractive = hasSubItems || hasLink;

  return (
    <div
      className={labelContainerClassNames}
      data-selected={isCurrentPage ? true : undefined}
      data-animation-part={AnimationParts.MenuItemContent}
      data-preview={partToPreviewStateMap?.item}
      data-testId={testIds.itemLabel}
    >
      <Link
        ref={linkRef}
        {...link}
        activateByKey="Enter"
        onClick={createSDKAction(item, isCurrentPage, onItemClick)}
        onMouseEnter={createSDKAction(item, isCurrentPage, onItemMouseIn)}
        onMouseLeave={createSDKAction(item, isCurrentPage, onItemMouseOut)}
        onDoubleClick={createSDKAction(item, isCurrentPage, onItemDblClick)}
        {...(isCurrentPage && {
          'aria-current': 'page',
        })}
        dataInteractive={isInteractive}
      >
        <div
          className={itemLabelClassNames}
          data-animation-part={AnimationParts.Label}
        >
          {label}
        </div>
      </Link>
      {hasDropdownMenu && (
        <button
          aria-label={translations.dropdownButtonAriaLabel}
          ref={chevronButtonRef}
          className={classNames(
            classes.dropdownToggleButton,
            !hasSubItems && classes.noDropdownItems,
            !hasLink && classes.coverAllSpace,
          )}
          onClick={handleDropdownIconClick}
          aria-expanded={isOpen}
          aria-haspopup={true}
        >
          <DropdownIcon
            data-animation-part={AnimationParts.DropdownIcon}
            className={classNames(
              classes.dropdownIcon,
              formatClassNames(menuSemanticClassNames.itemIcon),
            )}
          />
        </button>
      )}
    </div>
  );
};
